import { log } from 'log';
import { EventEmitterService } from 'js/services/eventEmitter.service';

import * as Types from './types';

export class WebSocketService<
    RequestData = any,
    ResponseData = any
> extends EventEmitterService<Types.TWebSocketServiceEventArgumentsMap<ResponseData>> {
    apiURL: string;
    isRawMessage?: boolean;
    socket: WebSocket | null = null;
    isReconnecting = false;
    reconnectTimeout = 15000;

    constructor({ apiURL }: { apiURL: string }) {
        super();
        this.apiURL = apiURL;
    }

    /**
     * public method
     * Creating socket connection
     * @returns void
     */
    createConnection = (): void => {
        if (this.socket) {
            return;
        }

        if (!this.apiURL) {
            log('error', 'WebSocket API URL is not configured');

            return;
        }

        this.socket = new WebSocket(this.apiURL);

        this.addSubscriptions();
    };

    /**
     * public method
     * Manual reconnecting
     * @returns void
     */
    reconnect = (): void => {
        if (this.socket) {
            this.socket.close();
        }

        return this.createConnection();
    };

    reconnectWithTimeout = (): void => {
        if (this.isReconnecting) {
            return;
        }

        this.isReconnecting = true;

        setTimeout(() => {
            this.isReconnecting = false;
            this.createConnection();
        }, this.reconnectTimeout);
    };

    addSubscriptions = (): void => {
        if (!this.socket) {
            return;
        }

        this.socket.addEventListener('open', this.handleOpen);
        this.socket.addEventListener('error', this.handleError);
        this.socket.addEventListener('close', this.handleClose);
        this.socket.addEventListener('message', this.handleMessage);
    };

    removeSubscriptions = (): void => {
        if (!this.socket) {
            return;
        }

        this.socket.removeEventListener('open', this.handleOpen);
        this.socket.removeEventListener('error', this.handleError);
        this.socket.removeEventListener('close', this.handleClose);
        this.socket.removeEventListener('message', this.handleMessage);
    };

    handleOpen = (): void => {
        const prevIsReconnecting = this.isReconnecting;

        this.isReconnecting = false;

        this.trigger('open');

        if (prevIsReconnecting) {
            this.trigger('reconnected');
        }
    };

    handleError = (event: Event): void => {
        this.removeSubscriptions();
        this.socket = null;

        this.trigger('error', event);

        this.reconnectWithTimeout();
    };

    handleClose = (event: CloseEvent): void => {
        this.removeSubscriptions();
        this.socket = null;

        this.trigger('close', event);

        this.reconnectWithTimeout();
    };

    send = (request: RequestData): void => {
        if (!this.isReconnecting && this.socket) {
            this.socket.send(JSON.stringify(request));
        }
    };

    handleMessage = ({ data }: { data: ResponseData | string }): void => {
        let response = data;

        if (!this.isRawMessage && typeof data === 'string') {
            try {
                response = JSON.parse(data) as ResponseData;
            } catch {
                log('error', 'unable to parse WS message', data);
            }
        }

        this.trigger('message', response as ResponseData);
    };
}
